/**
 * LoadingSkeletons Component
 *
 * Provides loading skeletons for different sections of the Database Operations Page.
 *
 * Phase 6: Frontend Polish
 *
 * Features:
 * - Skeleton screens for all major components
 * - Smooth pulse animation
 * - Match actual component layouts
 * - Improve perceived performance
 */

import React from 'react';

// Base Skeleton Component
interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  variant?: 'text' | 'rect' | 'circle';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width,
  height,
  variant = 'rect',
}) => {
  const baseClass = 'skeleton';
  const variantClass = `skeleton-${variant}`;

  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return <div className={`${baseClass} ${variantClass} ${className}`} style={style} />;
};

// Overview Cards Loading Skeleton
export const OverviewCardsSkeleton: React.FC = () => {
  return (
    <div className="skeleton-overview-cards">
      {[1, 2, 3, 4].map((index) => (
        <div key={index} className="skeleton-card">
          <div className="skeleton-card-header">
            <Skeleton variant="circle" width={40} height={40} />
            <Skeleton width="60%" height={20} />
          </div>
          <div className="skeleton-card-body">
            <Skeleton width="80%" height={32} />
            <Skeleton width="50%" height={16} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Freshness Timeline Loading Skeleton
export const FreshnessTimelineSkeleton: React.FC = () => {
  return (
    <div className="skeleton-freshness-timeline">
      <Skeleton width="30%" height={24} className="mb-4" />
      {[1, 2, 3, 4].map((index) => (
        <div key={index} className="skeleton-timeline-item">
          <Skeleton width={120} height={16} />
          <Skeleton width="100%" height={8} />
          <Skeleton width={150} height={14} />
        </div>
      ))}
    </div>
  );
};

// Tables Tab Loading Skeleton
export const TablesTabSkeleton: React.FC = () => {
  return (
    <div className="skeleton-tables-tab">
      {/* Search and filters */}
      <div className="skeleton-tables-header">
        <Skeleton width={300} height={40} />
        <div className="skeleton-tables-actions">
          <Skeleton width={120} height={40} />
          <Skeleton width={120} height={40} />
        </div>
      </div>

      {/* Table */}
      <div className="skeleton-table">
        {/* Table header */}
        <div className="skeleton-table-header">
          {[1, 2, 3, 4, 5, 6].map((index) => (
            <Skeleton key={index} width="100%" height={20} />
          ))}
        </div>

        {/* Table rows */}
        {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
          <div key={row} className="skeleton-table-row">
            {[1, 2, 3, 4, 5, 6].map((col) => (
              <Skeleton key={col} width="80%" height={16} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Performance Tab Loading Skeleton
export const PerformanceTabSkeleton: React.FC = () => {
  return (
    <div className="skeleton-performance-tab">
      {/* Query Performance Section */}
      <div className="skeleton-section">
        <Skeleton width="40%" height={24} className="mb-4" />
        <div className="skeleton-stats-grid">
          {[1, 2, 3, 4].map((index) => (
            <div key={index} className="skeleton-stat-card">
              <Skeleton width="60%" height={16} />
              <Skeleton width="80%" height={28} />
            </div>
          ))}
        </div>
      </div>

      {/* Connection Pool Section */}
      <div className="skeleton-section">
        <Skeleton width="40%" height={24} className="mb-4" />
        <div className="skeleton-chart">
          <Skeleton width="100%" height={250} />
        </div>
      </div>

      {/* Slow Queries Section */}
      <div className="skeleton-section">
        <Skeleton width="40%" height={24} className="mb-4" />
        <div className="skeleton-table">
          {[1, 2, 3, 4, 5].map((row) => (
            <div key={row} className="skeleton-table-row">
              <Skeleton width="70%" height={16} />
              <Skeleton width="15%" height={16} />
              <Skeleton width="10%" height={16} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Jobs Tab Loading Skeleton
export const JobsTabSkeleton: React.FC = () => {
  return (
    <div className="skeleton-jobs-tab">
      {/* Status Summary */}
      <div className="skeleton-status-summary">
        {[1, 2, 3].map((index) => (
          <div key={index} className="skeleton-status-card">
            <Skeleton width={60} height={60} variant="circle" />
            <Skeleton width="80%" height={20} />
          </div>
        ))}
      </div>

      {/* Search and filters */}
      <div className="skeleton-jobs-header">
        <Skeleton width={300} height={40} />
        <Skeleton width={150} height={40} />
      </div>

      {/* Job Cards */}
      <div className="skeleton-jobs-grid">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
          <div key={index} className="skeleton-job-card">
            <div className="skeleton-job-header">
              <Skeleton width="70%" height={20} />
              <Skeleton width={80} height={24} variant="rect" />
            </div>
            <Skeleton width="50%" height={16} className="mb-2" />
            <Skeleton width="60%" height={16} className="mb-2" />
            <div className="skeleton-job-actions">
              <Skeleton width={100} height={32} />
              <Skeleton width={100} height={32} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Cleanup Tab Loading Skeleton
export const CleanupTabSkeleton: React.FC = () => {
  return (
    <div className="skeleton-cleanup-tab">
      {/* Current Status */}
      <div className="skeleton-section">
        <Skeleton width="40%" height={24} className="mb-4" />
        <div className="skeleton-cleanup-status">
          <Skeleton width="100%" height={150} />
        </div>
      </div>

      {/* History */}
      <div className="skeleton-section">
        <Skeleton width="40%" height={24} className="mb-4" />
        <div className="skeleton-table">
          {[1, 2, 3, 4, 5].map((row) => (
            <div key={row} className="skeleton-table-row">
              {[1, 2, 3, 4, 5, 6].map((col) => (
                <Skeleton key={col} width="80%" height={16} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Generic Card Loading Skeleton
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="skeleton-card">
          <Skeleton width="60%" height={20} className="mb-3" />
          <Skeleton width="100%" height={16} className="mb-2" />
          <Skeleton width="80%" height={16} />
        </div>
      ))}
    </>
  );
};

// Styles (add to database.css)
export const loadingSkeletonsStyles = `
/* Base Skeleton */
.skeleton {
  background: linear-gradient(
    90deg,
    var(--skeleton-base, #f0f0f0) 25%,
    var(--skeleton-highlight, #e0e0e0) 50%,
    var(--skeleton-base, #f0f0f0) 75%
  );
  background-size: 200% 100%;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
  border-radius: 4px;
}

@keyframes skeleton-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-text {
  height: 16px;
  margin-bottom: 8px;
}

.skeleton-rect {
  /* Default rectangular shape */
}

.skeleton-circle {
  border-radius: 50%;
}

/* Overview Cards Skeleton */
.skeleton-overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.skeleton-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.skeleton-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.skeleton-card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Freshness Timeline Skeleton */
.skeleton-freshness-timeline {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.skeleton-timeline-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
  border-bottom: 1px solid var(--color-border, #e5e7eb);
}

.skeleton-timeline-item:last-child {
  border-bottom: none;
}

/* Tables Tab Skeleton */
.skeleton-tables-tab {
  background: white;
  border-radius: 12px;
  padding: 24px;
}

.skeleton-tables-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.skeleton-tables-actions {
  display: flex;
  gap: 12px;
}

.skeleton-table {
  width: 100%;
}

.skeleton-table-header {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 2px solid var(--color-border, #e5e7eb);
  margin-bottom: 12px;
}

.skeleton-table-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border, #e5e7eb);
}

/* Performance Tab Skeleton */
.skeleton-performance-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.skeleton-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
}

.skeleton-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.skeleton-stat-card {
  padding: 16px;
  background: var(--color-bg-secondary, #f9fafb);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-chart {
  margin-top: 16px;
}

/* Jobs Tab Skeleton */
.skeleton-jobs-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.skeleton-status-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  padding: 20px;
  background: white;
  border-radius: 12px;
}

.skeleton-status-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
}

.skeleton-jobs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: white;
  border-radius: 12px;
}

.skeleton-jobs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.skeleton-job-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.skeleton-job-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.skeleton-job-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

/* Cleanup Tab Skeleton */
.skeleton-cleanup-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.skeleton-cleanup-status {
  background: var(--color-bg-secondary, #f9fafb);
  border-radius: 8px;
  padding: 24px;
}

/* Utility Classes */
.mb-2 {
  margin-bottom: 8px;
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-4 {
  margin-bottom: 16px;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .skeleton-overview-cards {
    grid-template-columns: 1fr;
  }

  .skeleton-tables-header {
    flex-direction: column;
    gap: 12px;
  }

  .skeleton-table-header,
  .skeleton-table-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .skeleton-stats-grid {
    grid-template-columns: 1fr;
  }

  .skeleton-jobs-grid {
    grid-template-columns: 1fr;
  }
}
`;

// Example usage:
/*
import {
  OverviewCardsSkeleton,
  TablesTabSkeleton,
  PerformanceTabSkeleton,
  JobsTabSkeleton,
  CleanupTabSkeleton
} from './LoadingSkeletons';

// In your component:
{isLoading ? <OverviewCardsSkeleton /> : <OverviewCards data={data} />}

// With error boundaries:
<ErrorBoundary>
  {isLoading ? <TablesTabSkeleton /> : <TablesTab data={data} />}
</ErrorBoundary>
*/
